import {
  EntityId,
  PrinticularProductTemplatePage,
  PrinticularProductTemplateVariant,
  PrintServiceProductEntity,
  PrintServiceProductEntityHydrated,
  PrintServiceProductImageEntity,
} from "@jackfruit/common"
import { SagaIterator } from "@redux-saga/types"
import { nanoid, PayloadAction } from "@reduxjs/toolkit"
import { chunk, first, isEqual, last, slice } from "lodash"
import {
  actionChannel,
  call,
  delay,
  getContext,
  put,
  select,
  take,
  takeEvery,
} from "redux-saga/effects"
import { createImageFromProduct } from "~/components/blocks/product-feed/utils/imageGeneration"
import {
  getDefaultInnerPageSpreadForProduct,
  getMinAndMaxImagesForPhotoBookProduct,
  isProductAPhotoBook,
  photoBookHasCustomCover,
  resolveInsideTemplatePages,
} from "~/components/editors/photo-book/PhotoBookUtils"
import { CartEntity } from "~/interfaces/entities/Cart"
import { ImageEntity } from "~/interfaces/entities/Image"
import { ImageRegionEntity } from "~/interfaces/entities/ImageRegion"
import { LineItemEntity } from "~/interfaces/entities/LineItem"
import { PageSessionEntity } from "~/interfaces/entities/PageSession"
import {
  ProductPageEntity,
  ProductPageEntityHydrated,
} from "~/interfaces/entities/ProductPage"
import { UploadEntity } from "~/interfaces/entities/Upload"
import { getPrintServiceProductImages } from "~/redux/saga/printServiceProductImages"
import { Files } from "~/services/Files"
import { getPagesFromProduct } from "~/services/PrintServiceProductHelpers"
import { findDefaultProductForImage, scrollTo } from "~/services/Utils"
import {
  actions,
  CreateLineItemForProductIdAndTemplateIdPayload,
  CreateLineItemForProductIdAndTemplateIdWithPlaceholderImagePayload,
  CreateLineItemForProductIdPayload,
  CreateLineItemForProductIdWithImageUrlPayload,
  CreateLineItemForProductIdWithPlaceholderImagePayload,
  CreateLineItemFromButtonPayload,
  CreateLineItemFromTemplatePayload,
  CreateLineItemPayload,
  FillLineItemImageRegionsWithImageUrlsPayload,
  InsertPageSpreadToLineItemPayload,
  RemoveIncompletePageSpreadsFromLineItemPayload,
  RemoveLineItemPayload,
  removePageSpreadFromLineItem,
  RemovePageSpreadFromLineItemPayload,
  ReplaceImageRegionImageWithImageUrlPayload,
  SwapImageRegionFromPageAToPageBPayload,
  UploadImagesAndCreateLineItemsForProductIdAndTemplateIdPayload,
  UploadImagesAndCreateLineItemsForProductIdPayload,
} from "../process"
import { blockProductFeedActions } from "../state/blockProductFeeds"
import { carts, cartsSelector } from "../state/carts"
import { giftCertificates } from "../state/giftCertificates"
import { imageRegions, imageRegionsSelectors } from "../state/imageRegions"
import { lineItems, lineItemsSelectors } from "../state/lineItems"
import { pageSessions } from "../state/pageSessions"
import { printServiceProductImagesSelector } from "../state/printServiceProductImages"
import { printServiceProductsSelector } from "../state/printServiceProducts"
import { productPages } from "../state/productPages"
import { textRegions } from "../state/textRegions"
import { RootState } from "../store"
import { addLineItemToCart, getCurrentCart } from "./cart"
import {
  getAllImageRegionsForLineItem,
  getAllImageRegionsForProductPageIds,
  getImageRegion,
} from "./imageRegion"
import {
  convertImageToJpegIFNecessary,
  createUpload,
  getImage,
  resetImageTransformation,
} from "./images"
import {
  createLineItem,
  getLineItem,
  getLineItemPageSpreads,
  updateLineItem,
  updateLineItemFromUploadWithSingleProduct,
  updateLineItemWithUploads,
} from "./lineItems"
import { attachUploadToPageSession, getCurrentPageSession } from "./pageSession"
import {
  getAvailableProductsForCurrentPage,
  getPageProductsForFulfillment,
  getPrintServiceProduct,
} from "./printServiceProducts"
import { AnalyticAction, processAnalyticEvents } from "./processAnalyticEvents"
import { updateFulfillment } from "./processUpdateFulfillment"
import { processUpdateImageRegionImage } from "./processUpdateImageRegionImage"
import { autoDetectOrientationForLineItem } from "./processUpdateLineItemOrientation"
import { getProductPage } from "./productPages"
import { getProductTemplate } from "./productTemplates"
import { getProductTemplateVariantForProduct } from "./productTemplateVariants"
import {
  createImageRegionFromTemplate,
  createTextRegionFromTemplate,
  preparePhotoBookPagesForVariant,
  prepareProductPagesForVariant,
} from "./templateHelpers"
import { duplicateUpload, getUpload, waitForUploadReady } from "./uploads"

// ====================================================
// Create one line item with default configuration
// from selected product
// ====================================================
export function* watchCreateLineItem() {
  yield takeEvery(actions.createLineItem.type, processCreateLineItem)
}

export function* processCreateLineItem(
  action: PayloadAction<CreateLineItemPayload>
): SagaIterator<any> {
  const { productId, createFromProduct } = action.payload

  const getCurrentPageId = yield getContext("getCurrentPageId")
  const currentPageId = getCurrentPageId()

  const cart: CartEntity = yield select((state: RootState) =>
    cartsSelector.selectById(state, currentPageId)
  )

  const printServiceProduct: PrintServiceProductEntity = yield select(
    (state: RootState) =>
      printServiceProductsSelector.selectById(state, productId)
  )

  const printServiceProductImages: PrintServiceProductImageEntity[] =
    yield select((state: RootState) =>
      printServiceProductImagesSelector.selectByIds(
        state,
        printServiceProduct.printServiceProductImages
      )
    )

  const { addOne: addOneLineItem } = lineItems.actions
  const { updateOne: updateCart } = carts.actions

  const requiredProductPages = getPagesFromProduct({
    ...printServiceProduct,
    printServiceProductImages,
  })

  const productPageIds = []
  for (let i = 0; i < requiredProductPages.length; i++) {
    const requiredProductPage = requiredProductPages[i]
    const pageId: EntityId = yield call(createProductPage, {
      page: requiredProductPage,
    })
    productPageIds.push(pageId)
  }

  const lineItemId = nanoid()

  let orientation =
    printServiceProduct.metaData?.orientationOverride ?? "portrait"
  if (createFromProduct) {
    orientation =
      printServiceProduct.width < printServiceProduct.height
        ? "portrait"
        : "landscape"
  }

  const giftCertificateId =
    printServiceProduct.categoryName === "gift certificate"
      ? nanoid()
      : undefined

  const giftCertificateProductId =
    printServiceProduct.categoryName === "gift certificate"
      ? printServiceProduct.id
      : undefined

  //Create gift certificate
  if (giftCertificateId && giftCertificateProductId) {
    yield put(
      giftCertificates.actions.addOne({
        id: giftCertificateId,
        productId: Number(giftCertificateProductId),
        name: "",
        email: "",
        message: "",
      })
    )
  }

  // create line item
  yield put(
    addOneLineItem({
      id: lineItemId,
      productPageIds,
      productId,
      quantity: 1,
      orientation,
      isReadOnly: false,
      renderedUploadIds: [],
      isTemplate: false,
      giftCertificateId,

      // Open editor straight away if a product was selected instead of an image being uploaded
      isEditing: createFromProduct ? true : false,
      isReady: createFromProduct ? true : false,
      isConfirmed: createFromProduct ? false : true,
    })
  )

  // add line item to the current page's cart
  yield put(
    updateCart({
      id: cart.id,
      changes: {
        lineItemIds: [...cart.lineItemIds, lineItemId],
      },
    })
  )

  yield call(
    processAnalyticEvents,
    AnalyticAction({
      eventType: "addOneItemToCart",
      data: { lineItemId },
    })
  )

  yield put(
    actions.updateOrderSummary({
      reason: "new line item created",
      reasonType: "cartChange",
    })
  )

  return lineItemId
}

export function* createProductPage({
  page,
  templatePage,
}: {
  page: ProductPageEntityHydrated
  templatePage?: PrinticularProductTemplatePage
}): SagaIterator<EntityId> {
  const pageId = nanoid()

  const { addOne: addOneImageRegion } = imageRegions.actions
  const { addOne: addOneTextRegion } = textRegions.actions
  const { addOne: addOneProductPage } = productPages.actions

  const imageRegionIds: EntityId[] = []
  const textRegionIds: EntityId[] = []

  if (templatePage) {
    for (const pageImageRegion of templatePage.imageRegions ?? []) {
      const imageRegionId = yield call(createImageRegionFromTemplate, {
        pageImageRegion,
      })
      imageRegionIds.push(imageRegionId)
    }

    for (const pageTextRegion of templatePage.textRegions ?? []) {
      const textRegionId = yield call(createTextRegionFromTemplate, {
        pageTextRegion,
      })
      textRegionIds.push(textRegionId)
    }

    yield put(
      productPages.actions.addOne({
        id: pageId,
        pageType: page.pageType,
        width: templatePage.width,
        height: templatePage.height,
        svg: templatePage.svg,
        imageRegionIds,
        textRegionIds,
        seeThrough: page.seeThrough ?? false,
        isCustomCover: page.isCustomCover ?? false,
      })
    )
  } else {
    for (const imageRegion of page.imageRegions) {
      const id = nanoid()
      yield put(
        addOneImageRegion({
          id,
          window: imageRegion.window,
          imageFitMode: imageRegion.imageFitMode,
          isClampEnabled: imageRegion.isClampEnabled,
        })
      )
      imageRegionIds.push(id)
    }

    for (const textRegion of page.textRegions) {
      const textRegionId = nanoid()
      yield put(
        addOneTextRegion({
          id: textRegionId,
          window: textRegion.window,
          align: textRegion.align,
          color: textRegion.color,
          font: textRegion.font,
          key: textRegion.key,
          placeholder: textRegion.placeholder,
          size: textRegion.size,
          defaultSize: textRegion.defaultSize,
          text: "",
        })
      )
      textRegionIds.push(textRegionId)
    }

    yield put(
      addOneProductPage({
        id: pageId,
        pageType: page.pageType,
        width: page.width,
        height: page.height,
        seeThrough: page.seeThrough ?? false,
        isCustomCover: page.isCustomCover ?? false,
        isVirtualPage: page.isVirtualPage ?? false,
        imageRegionIds,
        textRegionIds,
      })
    )
  }

  return pageId
}

// ====================================================
// create line item from a template
// ====================================================
export function* watchCreateLineItemFromTemplate() {
  yield takeEvery(
    actions.createLineItemFromTemplate.type,
    processCreateLineItemFromTemplate
  )
}

function* processCreateLineItemFromTemplate(
  action: PayloadAction<CreateLineItemFromTemplatePayload>
): SagaIterator<any> {
  const {
    productTemplateId,
    productId,
    blockTemplateId,
    blockId,
    shouldOpenEditor = true,
  } = action.payload

  const getCurrentPageId = yield getContext("getCurrentPageId")
  const currentPageId = getCurrentPageId()

  const cart: CartEntity = yield select((state: RootState) =>
    cartsSelector.selectById(state, currentPageId)
  )

  const printServiceProduct: PrintServiceProductEntity = yield select(
    (state: RootState) =>
      printServiceProductsSelector.selectById(state, productId)
  )
  const isPhotoBook = printServiceProduct.categoryName === "photo-book"

  const variants = yield call(getProductTemplateVariantForProduct, {
    productId,
    productTemplateId,
  })
  const [firstVariant] = variants
  const [firstProductPage] = firstVariant.variant.pages
  const defaultOrientation =
    firstProductPage.width > firstProductPage.height ? "landscape" : "portrait"

  const orientation =
    printServiceProduct.metaData?.orientationOverride ?? defaultOrientation

  // create product pages based on the template variant
  let productPageIds: EntityId[]

  if (isPhotoBook) {
    const printServiceProductImages = yield select((state: RootState) =>
      printServiceProductImagesSelector.selectByIds(
        state,
        printServiceProduct.printServiceProductImages
      )
    )

    productPageIds = yield call(preparePhotoBookPagesForVariant, {
      product: { ...printServiceProduct, printServiceProductImages },
      variant: firstVariant.variant,
    })
  } else {
    productPageIds = yield call(prepareProductPagesForVariant, {
      productPageIds: [],
      variant: firstVariant.variant,
    })
  }

  const { addOne: addOneLineItem } = lineItems.actions
  const { updateOne: updateCart } = carts.actions

  const lineItemId = nanoid()

  // Create line item
  yield put(
    addOneLineItem({
      id: lineItemId,
      productPageIds,
      productId,
      productTemplateId,
      blockTemplateId,
      blockId,
      productTemplateVariantId: firstVariant.variant.key,
      quantity: 1,
      orientation,
      isEditing: shouldOpenEditor,
      isTemplate: true,
      isConfirmed: false,
      isReadOnly: false,
      isReady: true,
      renderedUploadIds: [],
    })
  )

  // Add line item to the current page's cart
  yield put(
    updateCart({
      id: cart.id,
      changes: {
        lineItemIds: [...cart.lineItemIds, lineItemId],
      },
    })
  )

  yield call(
    processAnalyticEvents,
    AnalyticAction({
      eventType: "addOneItemToCart",
      data: { lineItemId },
    })
  )

  yield put(
    actions.updateOrderSummary({
      reason: "lineItem created from template",
      reasonType: "cartChange",
    })
  )

  return lineItemId
}

export function* watchRemoveLineItem() {
  yield takeEvery(actions.removeLineItem.type, processRemoveLineItem)
}

export function* processRemoveLineItem(
  action: PayloadAction<RemoveLineItemPayload>
) {
  const { lineItemId } = action.payload
  const cart: CartEntity = yield call(getCurrentCart)

  const newLineItemIds = cart.lineItemIds.filter(id => id !== lineItemId)

  const lineItem: LineItemEntity = yield select((state: RootState) =>
    lineItemsSelectors.selectById(state, lineItemId)
  )

  const pageSession: PageSessionEntity = yield call(getCurrentPageSession)

  if (lineItem.giftCertificateId !== undefined) {
    yield put(giftCertificates.actions.removeOne(lineItem.giftCertificateId))
  }

  if (newLineItemIds.length === 0) {
    yield put(
      pageSessions.actions.updateOne({
        id: pageSession.id,
        changes: {
          uploadIds: [],
        },
      })
    )
  }

  yield put(
    carts.actions.updateOne({
      id: cart.id,
      changes: {
        lineItemIds: newLineItemIds,
      },
    })
  )

  yield call(
    processAnalyticEvents,
    AnalyticAction({
      eventType: "clearItemFromCart",
      data: { lineItemId },
    })
  )

  yield put(lineItems.actions.removeOne(lineItemId))

  const hasAStoreSelected = Boolean(cart.storeId)

  //refresh store search after deleting a line item
  //for product-first flow only
  if (
    pageSession.pageFlow === "product-first" &&
    cart.fulfillment === "pickup" &&
    hasAStoreSelected
  ) {
    yield put(actions.searchStores({}))
  }

  yield put(
    actions.updateOrderSummary({
      reason: "lineItem removed",
      reasonType: "cartChange",
    })
  )
}

export function* watchAddLineItemToCart(): SagaIterator {
  const requestChannel = yield actionChannel(actions.addLineItemToCart.type)
  while (true) {
    const { payload } = yield take(requestChannel)
    yield call(addLineItemToCart, payload)
  }
}

export function* watchInsertPageSpreadToLineItemBefore() {
  yield takeEvery(
    actions.insertPageSpreadToLineItemBefore.type,
    processInsertPageSpreadToLineItemBefore
  )
}

export function* watchInsertPageSpreadToLineItemAfter() {
  yield takeEvery(
    actions.insertPageSpreadToLineItemAfter.type,
    processInsertPageSpreadToLineItemAfter
  )
}

function* processInsertPageSpreadToLineItemBefore(
  action: PayloadAction<InsertPageSpreadToLineItemPayload>
): SagaIterator<any> {
  const { lineItemId, productPageId, callback } = action.payload

  const lineItem: LineItemEntity = yield call(() => getLineItem(lineItemId))
  const currentPageIndex = lineItem.productPageIds.indexOf(productPageId)

  // [cover, 0 | 1, 2 | 3, 4 | 5, cover]
  // find smallest odd number
  let insertPosition = currentPageIndex
  const isEven = Number(currentPageIndex) % 2 === 0
  if (isEven) {
    insertPosition = currentPageIndex - 1
  }

  const newPageIds: [EntityId, EntityId] = yield call(() =>
    insertPageSpreadToLineItem(lineItemId, insertPosition, callback)
  )

  return newPageIds
}

function* processInsertPageSpreadToLineItemAfter(
  action: PayloadAction<InsertPageSpreadToLineItemPayload>
): SagaIterator<any> {
  const { lineItemId, productPageId, callback } = action.payload

  const lineItem: LineItemEntity = yield call(() => getLineItem(lineItemId))
  const currentPageIndex = lineItem.productPageIds.indexOf(productPageId)

  // [cover, 0 | 1, 2 | 3, 4 | 5, cover]
  // find the next slot to insert
  let insertPosition = currentPageIndex + 2
  const isEven = Number(currentPageIndex) % 2 === 0
  if (isEven) {
    insertPosition = currentPageIndex + 1
  }

  const newPageIds: [EntityId, EntityId] = yield call(() =>
    insertPageSpreadToLineItem(lineItemId, insertPosition, callback)
  )

  return newPageIds
}

export function* insertPageSpreadToLineItem(
  lineItemId: EntityId,
  position: number,
  callback?: (id: EntityId) => void
) {
  const lineItem: LineItemEntity = yield call(() => getLineItem(lineItemId))
  const productId = lineItem.productId

  const printServiceProduct: PrintServiceProductEntity = yield select(
    (state: RootState) =>
      printServiceProductsSelector.selectById(state, productId)
  )

  const printServiceProductImages: PrintServiceProductImageEntity[] =
    yield select((state: RootState) =>
      printServiceProductImagesSelector.selectByIds(
        state,
        printServiceProduct.printServiceProductImages
      )
    )

  const { leftPage, rightPage } = getDefaultInnerPageSpreadForProduct({
    ...printServiceProduct,
    printServiceProductImages,
  })

  // find page template if there is one
  let leftTemplatePage: PrinticularProductTemplatePage | undefined
  let rightTemplatePage: PrinticularProductTemplatePage | undefined

  if (lineItem.productTemplateId && lineItem.productTemplateVariantId) {
    const productTemplate: { variants: PrinticularProductTemplateVariant[] } =
      yield call(getProductTemplate, lineItem.productTemplateId)

    const variant = productTemplate.variants.find(
      (variant: PrinticularProductTemplateVariant) =>
        variant.key === lineItem.productTemplateVariantId
    )

    if (variant) {
      const {
        agnosticTemplatePages,
        leftTemplatePage: left,
        rightTemplatePage: right,
      } = resolveInsideTemplatePages(variant)
      const hasCustomCover = photoBookHasCustomCover(printServiceProductImages)

      const leftPagePosition = hasCustomCover ? position - 1 : position
      const rightPagePosition = hasCustomCover ? position : position + 1

      const leftAgnosticPage =
        agnosticTemplatePages[leftPagePosition % agnosticTemplatePages.length]
      const rightAgnosticPage =
        agnosticTemplatePages[rightPagePosition % agnosticTemplatePages.length]

      leftTemplatePage = left ?? leftAgnosticPage
      rightTemplatePage = right ?? rightAgnosticPage
    }
  }

  const newPageOneId: EntityId = yield call(createProductPage, {
    page: leftPage,
    templatePage: leftTemplatePage,
  })
  const newPageTwoId: EntityId = yield call(createProductPage, {
    page: rightPage,
    templatePage: rightTemplatePage,
  })

  const { productPageIds } = lineItem

  const newProductPageIds = [
    ...productPageIds.slice(0, position),
    newPageOneId,
    newPageTwoId,
    ...productPageIds.slice(position),
  ]

  const { updateOne: updateLineItem } = lineItems.actions

  yield put(
    updateLineItem({
      id: lineItemId,
      changes: {
        productPageIds: newProductPageIds,
      },
    })
  )

  callback && callback(newPageOneId)

  return [newPageOneId, newPageTwoId]
}

export function* watchRemovePageSpreadFromLineItem() {
  yield takeEvery(
    actions.removePageSpreadFromLineItem.type,
    processRemovePageSpreadFromLineItem
  )
}

function* processRemovePageSpreadFromLineItem(
  action: PayloadAction<RemovePageSpreadFromLineItemPayload>
) {
  const { payload } = action

  const { lineItemId, productPageId } = payload
  const lineItem: LineItemEntity = yield call(() => getLineItem(lineItemId))

  // remove the product page id from the line item
  const { productPageIds } = lineItem
  // product page is always left page
  const nextPageIndex = productPageIds.indexOf(productPageId) + 1
  const nextPageId = productPageIds[nextPageIndex]
  const newProductPageIds = productPageIds.filter(
    id => ![productPageId, nextPageId].includes(id)
  )

  const { updateOne: updateLineItem } = lineItems.actions

  yield put(
    updateLineItem({
      id: lineItemId,
      changes: {
        productPageIds: newProductPageIds,
      },
    })
  )
}

export function* watchRemoveIncompletePageSpreadsFromLineItem() {
  yield takeEvery(
    actions.removeIncompletePageSpreadsFromLineItem.type,
    processRemoveIncompletePageSpreadsFromLineItem
  )
}

function* processRemoveIncompletePageSpreadsFromLineItem(
  action: PayloadAction<RemoveIncompletePageSpreadsFromLineItemPayload>
) {
  const { payload } = action
  const { lineItemId } = payload

  const lineItem: LineItemEntity = yield call(() => getLineItem(lineItemId))
  const { productPageIds } = lineItem

  const pageCheck: {
    productPageId: EntityId
    pageIsComplete: boolean
  }[] = []

  // check if page contains an image or not, if not, remove it
  for (const productPageId of productPageIds) {
    const allImageRegionsForPage: ImageRegionEntity[] = []

    const productPage: ProductPageEntity = yield call(() =>
      getProductPage(productPageId)
    )
    const { imageRegionIds } = productPage
    for (const imageRegionId of imageRegionIds) {
      const imageRegion: ImageRegionEntity = yield call(() =>
        getImageRegion(imageRegionId)
      )
      allImageRegionsForPage.push(imageRegion)
    }

    const pageIsComplete = allImageRegionsForPage.every(imageRegion =>
      Boolean(imageRegion.uploadId)
    )

    pageCheck.push({
      productPageId,
      pageIsComplete,
    })
  }

  const spreadCheck: {
    productPageIds: EntityId[]
    isSpreadComplete: boolean
  }[] = []

  const firstPage = first(pageCheck)
  const middlePages = slice(pageCheck, 1, pageCheck.length - 1)
  const lastPage = last(pageCheck)

  if (firstPage && lastPage && middlePages.length > 0) {
    spreadCheck.push({
      productPageIds: [firstPage.productPageId],
      isSpreadComplete: firstPage.pageIsComplete,
    })

    const middleSpreads = chunk(middlePages, 2)

    for (const spread of middleSpreads) {
      const [leftPage, rightPage] = spread
      spreadCheck.push({
        productPageIds: [leftPage.productPageId, rightPage.productPageId],
        isSpreadComplete: leftPage.pageIsComplete && rightPage.pageIsComplete,
      })
    }

    spreadCheck.push({
      productPageIds: [lastPage.productPageId],
      isSpreadComplete: lastPage.pageIsComplete,
    })
  }

  for (const spread of spreadCheck) {
    if (!spread.isSpreadComplete) {
      const rightPageIdOfSpread = spread.productPageIds[1]
      yield call(() =>
        processRemovePageSpreadFromLineItem({
          type: removePageSpreadFromLineItem.type,
          payload: {
            lineItemId,
            productPageId: rightPageIdOfSpread,
          },
        })
      )
    }
  }
}

export function* watchCreateLineItemFromButton() {
  yield takeEvery(
    actions.createLineItemFromButton.type,
    processCreateLineItemFromButton
  )
}

function* processCreateLineItemFromButton(
  action: PayloadAction<CreateLineItemFromButtonPayload>
): SagaIterator<any> {
  const { currentFulfillment, fulfillment, productId, onOpen, onClose } =
    action.payload

  const getCurrentPageId = yield getContext("getCurrentPageId")
  const pageId: EntityId = getCurrentPageId()

  const availableProductsForFulfillment = yield call(
    getPageProductsForFulfillment,
    { fulfillment, pageId }
  )
  const isProductAvailableForFulfillment = availableProductsForFulfillment.some(
    (product: PrintServiceProductEntityHydrated) => product.id === productId
  )

  if (!isProductAvailableForFulfillment) {
    console.error(
      `Product ${productId} is not available for ${fulfillment}, please check config.`
    )
    return
  }

  // if need to change fulfillment selection
  if (currentFulfillment !== fulfillment) {
    yield call(updateFulfillment, { pageId, fulfillment, onOpen, onClose })
  }

  yield put(actions.createLineItem({ productId, createFromProduct: true }))
}

export function* watchSwapImageRegionFromPageAToPageB() {
  yield takeEvery(
    actions.swapImageRegionFromPageAToPageB.type,
    processSwapImageRegionFromPageAToPageB
  )
}

function* processSwapImageRegionFromPageAToPageB(
  action: PayloadAction<SwapImageRegionFromPageAToPageBPayload>
) {
  const { pageAId, pageBId } = action.payload

  const pageA: ProductPageEntity = yield call(() => getProductPage(pageAId))
  const pageB: ProductPageEntity = yield call(() => getProductPage(pageBId))

  const { imageRegionIds: imageRegionIdsA } = pageA
  const { imageRegionIds: imageRegionIdsB } = pageB

  const imageRegionsA: ImageRegionEntity[] = yield select((state: RootState) =>
    imageRegionsSelectors.selectByIds(state, imageRegionIdsA)
  )
  const imageRegionsB: ImageRegionEntity[] = yield select((state: RootState) =>
    imageRegionsSelectors.selectByIds(state, imageRegionIdsB)
  )

  const count = Math.min(imageRegionsA.length, imageRegionsB.length)
  const longer =
    imageRegionsA.length > imageRegionsB.length ? imageRegionsA : imageRegionsB

  for (let i = 0; i < count; i++) {
    const imageRegionA = imageRegionsA[i]
    const imageRegionB = imageRegionsB[i]

    yield put(
      imageRegions.actions.updateOne({
        id: imageRegionA.id,
        changes: { uploadId: imageRegionB.uploadId },
      })
    )

    yield put(
      imageRegions.actions.updateOne({
        id: imageRegionB.id,
        changes: { uploadId: imageRegionA.uploadId },
      })
    )

    const sameImageFitMode =
      imageRegionA.imageFitMode === imageRegionB.imageFitMode

    const windowAreIdentical = isEqual(imageRegionA.window, imageRegionB.window)

    const shouldResetWindowAndTransformation =
      !windowAreIdentical || !sameImageFitMode

    if (shouldResetWindowAndTransformation) {
      yield call(resetImageTransformation, {
        imageRegionId: imageRegionA.id,
      })

      yield call(resetImageTransformation, {
        imageRegionId: imageRegionB.id,
      })
    }
  }

  for (let i = count; i < longer.length; i++) {
    yield put(
      imageRegions.actions.updateOne({
        id: longer[i].id,
        changes: { uploadId: undefined },
      })
    )
  }
}

// ============================================
// PRODUCT WITHOUT TEMPLATE
// ============================================

// ============================================
// upload image and create line item from
// product id
// ============================================
export function* watchUploadImagesAndCreateLineItemsForProductId() {
  yield takeEvery(
    actions.uploadImagesAndCreateLineItemsForProductId.type,
    processUploadImagesAndCreateLineItemsForProductId
  )
}

function* processUploadImagesAndCreateLineItemsForProductId(
  action: PayloadAction<UploadImagesAndCreateLineItemsForProductIdPayload>
) {
  const { productId, productFeedBlockId } = action.payload
  const files: File[] = yield call(Files.promptForImageUpload)
  const hasFiles = files.length > 0

  if (!hasFiles) {
    return
  }

  if (productFeedBlockId) {
    yield put(
      blockProductFeedActions.setIsLoadingUploadPhoto(productFeedBlockId, true)
    )
  }

  const cart: CartEntity = yield call(getCurrentCart)

  for (const file of files) {
    const convertedFile: File = yield call(convertImageToJpegIFNecessary, file)
    const { uploadId } = yield call(createUpload, { file: convertedFile })

    yield call(attachUploadToPageSession, uploadId)

    yield put(
      actions.queueImageForProcessing({
        id: uploadId,
        file: convertedFile,
      })
    )

    const lineItemId: EntityId = yield call(createLineItem)

    yield put(actions.addLineItemToCart({ lineItemId, cartId: cart.id }))

    yield call(updateLineItemFromUploadWithSingleProduct, {
      lineItemId,
      uploadIds: [uploadId],
      productId,
    })
  }

  yield put(
    actions.updateOrderSummary({
      reason: "created line item after upload",
      reasonType: "cartChange",
    })
  )

  yield call(scrollToCartOrStoreSearch)

  if (productFeedBlockId) {
    yield delay(500)
    yield put(
      blockProductFeedActions.setIsLoadingUploadPhoto(productFeedBlockId, false)
    )
  }
}

// ============================================
// create line item with placeholder image
// ============================================
export function* watchCreateLineItemForProductIdWithPlaceholderImage() {
  yield takeEvery(
    actions.createLineItemForProductIdWithPlaceholderImage.type,
    processCreateLineItemForProductIdWithPlaceholderImage
  )
}

function* processCreateLineItemForProductIdWithPlaceholderImage(
  action: PayloadAction<CreateLineItemForProductIdWithPlaceholderImagePayload>
) {
  const { productId, productFeedBlockId } = action.payload
  if (productFeedBlockId) {
    yield put(
      blockProductFeedActions.setIsLoadingBuyNow(productFeedBlockId, true)
    )
  }

  const cart: CartEntity = yield call(getCurrentCart)

  const product: PrintServiceProductEntityHydrated = yield call(
    getPrintServiceProduct,
    productId
  )
  const convertedFile: File = yield call(createImageFromProduct, product)

  const { uploadId } = yield call(createUpload, { file: convertedFile })
  yield call(attachUploadToPageSession, uploadId)
  yield put(
    actions.queueImageForProcessing({
      id: uploadId,
      file: convertedFile,
    })
  )

  yield call(waitForUploadReady, uploadId)

  const lineItemId: EntityId = yield call(createLineItem)
  yield call(updateLineItem, { productId, lineItemId })

  const allImageRegions: ImageRegionEntity[] = yield call(
    getAllImageRegionsForLineItem,
    lineItemId
  )

  const allUploadIds: EntityId[] = []
  for (const _imageRegion of allImageRegions) {
    const duplicatedUpload: UploadEntity = yield call(duplicateUpload, uploadId)
    allUploadIds.push(duplicatedUpload.id)
  }

  yield call(updateLineItemWithUploads, {
    lineItemId,
    uploadIds: allUploadIds,
    productId,
  })

  yield put(actions.addLineItemToCart({ lineItemId, cartId: cart.id }))

  yield put(
    actions.updateOrderSummary({
      reason: "created line item after upload",
      reasonType: "cartChange",
    })
  )

  yield call(scrollToCartOrStoreSearch)

  if (productFeedBlockId) {
    yield delay(500)
    yield put(
      blockProductFeedActions.setIsLoadingBuyNow(productFeedBlockId, false)
    )
  }
}

// ============================================
// create line item for product id and open editor
// ============================================
export function* watchCreateLineItemForProductId() {
  yield takeEvery(
    actions.createLineItemForProductId.type,
    processCreateLineItemForProductId
  )
}

function* processCreateLineItemForProductId(
  action: PayloadAction<CreateLineItemForProductIdPayload>
) {
  const { productId, productFeedBlockId } = action.payload
  if (productFeedBlockId) {
    yield put(
      blockProductFeedActions.setIsLoadingUploadPhoto(productFeedBlockId, true)
    )
  }

  const lineItemId: EntityId = yield call(
    processCreateLineItem,
    actions.createLineItem({
      productId,
      createFromProduct: true,
    })
  )

  if (productFeedBlockId) {
    yield delay(500)
    yield put(
      blockProductFeedActions.setIsLoadingUploadPhoto(productFeedBlockId, false)
    )
  }

  // open the editor
  yield put(
    lineItems.actions.updateOne({
      id: lineItemId,
      changes: {
        isEditing: true,
      },
    })
  )
}

// ============================================
// PRODUCT WITH TEMPLATE
// ============================================

// ============================================
// upload images and create line items from product
// and template
// ============================================
export function* watchUploadImagesAndCreateLineItemsForProductIdAndTemplateId() {
  yield takeEvery(
    actions.uploadImagesAndCreateLineItemsForProductIdAndTemplateId.type,
    processUploadImagesAndCreateLineItemsForProductIdAndTemplateId
  )
}

function* processUploadImagesAndCreateLineItemsForProductIdAndTemplateId(
  action: PayloadAction<UploadImagesAndCreateLineItemsForProductIdAndTemplateIdPayload>
) {
  const { productId, templateId, productFeedBlockId } = action.payload
  const files: File[] = yield call(Files.promptForImageUpload)
  const hasFiles = files.length > 0

  if (!hasFiles) {
    return
  }

  if (productFeedBlockId) {
    yield put(
      blockProductFeedActions.setIsLoadingUploadPhoto(productFeedBlockId, true)
    )
  }

  for (const file of files) {
    const convertedFile: File = yield call(convertImageToJpegIFNecessary, file)
    const { uploadId } = yield call(createUpload, { file: convertedFile })

    yield call(attachUploadToPageSession, uploadId)

    yield put(
      actions.queueImageForProcessing({
        id: uploadId,
        file: convertedFile,
      })
    )

    // create item with template
    const lineItemId: EntityId = yield call(
      processCreateLineItemFromTemplate,
      actions.createLineItemFromTemplate({
        productId,
        productTemplateId: templateId,
        shouldOpenEditor: false,
      })
    )

    // set the lineitem as confirmed
    // this prevent the line item from being removed
    // when hitting cancel in the editor
    yield put(
      lineItems.actions.updateOne({
        id: lineItemId,
        changes: {
          isConfirmed: true,
        },
      })
    )

    yield call(updateLineItemWithUploads, {
      lineItemId,
      uploadIds: [uploadId],
      productId,
    })
  }

  yield call(scrollToCartOrStoreSearch)

  if (productFeedBlockId) {
    yield delay(500)
    yield put(
      blockProductFeedActions.setIsLoadingUploadPhoto(productFeedBlockId, false)
    )
  }
}

// ============================================
// create a placeholder image from product and
// fill al template regions with it
// ============================================
export function* watchCreateLineItemForProductIdAndTemplateIdWithPlaceholderImage() {
  yield takeEvery(
    actions.createLineItemForProductIdAndTemplateIdWithPlaceholderImage.type,
    processCreateLineItemForProductIdAndTemplateIdWithPlaceholderImage
  )
}

function* processCreateLineItemForProductIdAndTemplateIdWithPlaceholderImage(
  action: PayloadAction<CreateLineItemForProductIdAndTemplateIdWithPlaceholderImagePayload>
) {
  const { productId, templateId, productFeedBlockId } = action.payload

  if (productFeedBlockId) {
    yield put(
      blockProductFeedActions.setIsLoadingBuyNow(productFeedBlockId, true)
    )
  }

  const product: PrintServiceProductEntityHydrated = yield call(
    getPrintServiceProduct,
    productId
  )
  const convertedFile: File = yield call(createImageFromProduct, product)

  // create upload for each image regions
  const { uploadId } = yield call(createUpload, { file: convertedFile })
  yield call(attachUploadToPageSession, uploadId)
  yield put(
    actions.queueImageForProcessing({
      id: uploadId,
      file: convertedFile,
    })
  )

  yield call(waitForUploadReady, uploadId)

  const lineItemId: EntityId = yield call(
    processCreateLineItemFromTemplate,
    actions.createLineItemFromTemplate({
      productId,
      productTemplateId: templateId,
      shouldOpenEditor: false,
    })
  )

  // set the lineitem as confirmed
  // this prevent the line item from being removed
  // when hitting cancel in the editor
  yield put(
    lineItems.actions.updateOne({
      id: lineItemId,
      changes: {
        isConfirmed: true,
      },
    })
  )

  const allImageRegions: ImageRegionEntity[] = yield call(
    getAllImageRegionsForLineItem,
    lineItemId
  )

  const allUploadIds: EntityId[] = []
  for (const _imageRegion of allImageRegions) {
    const duplicatedUpload: UploadEntity = yield call(duplicateUpload, uploadId)
    allUploadIds.push(duplicatedUpload.id)
  }

  yield call(updateLineItemWithUploads, {
    lineItemId,
    uploadIds: allUploadIds,
    productId,
  })

  yield call(scrollToCartOrStoreSearch)

  if (productFeedBlockId) {
    yield delay(500)
    yield put(
      blockProductFeedActions.setIsLoadingBuyNow(productFeedBlockId, false)
    )
  }
}

// ============================================
// create line item for product id and template id
// and open editor
// ============================================
export function* watchCreateLineItemForProductIdAndTemplateId() {
  yield takeEvery(
    actions.createLineItemForProductIdAndTemplateId.type,
    processCreateLineItemForProductIdAndTemplateId
  )
}

function* processCreateLineItemForProductIdAndTemplateId(
  action: PayloadAction<CreateLineItemForProductIdAndTemplateIdPayload>
) {
  const { productId, templateId, productFeedBlockId, shouldOpenEditor } =
    action.payload

  if (productFeedBlockId) {
    yield put(
      blockProductFeedActions.setIsLoadingUploadPhoto(productFeedBlockId, true)
    )
  }

  yield call(
    processCreateLineItemFromTemplate,
    actions.createLineItemFromTemplate({
      productId,
      productTemplateId: templateId,
      shouldOpenEditor,
    })
  )

  if (productFeedBlockId) {
    yield delay(500)
    yield put(
      blockProductFeedActions.setIsLoadingUploadPhoto(productFeedBlockId, false)
    )
  }
}

// ============================================
// create line item from image url
// ============================================
export function* watchCreateLineItemForProductIdWithImageUrl() {
  yield takeEvery(
    actions.createLineItemForProductIdWithImageUrl.type,
    processCreateLineItemForProductIdWithImageUrl
  )
}

function* processCreateLineItemForProductIdWithImageUrl(
  action: PayloadAction<CreateLineItemForProductIdWithImageUrlPayload>
) {
  try {
    const {
      imageUrls,
      onComplete,
      skipAutoScroll = false,
      productId: preferredProductId,
    } = action.payload

    const cart: CartEntity = yield call(getCurrentCart)

    const currentAvailableProducts: PrintServiceProductEntity[] = yield call(
      getAvailableProductsForCurrentPage
    )
    const canUsePreferredProductId = currentAvailableProducts.find(
      product => String(product.id) === String(preferredProductId)
    )

    const files: File[] = []

    for (const imageUrl of imageUrls) {
      const imageFile: File = yield call(Files.fetchImageFromUrl, imageUrl)
      files.push(imageFile)
    }

    for (const file of files) {
      const convertedFile: File = yield call(
        convertImageToJpegIFNecessary,
        file
      )
      const { uploadId } = yield call(createUpload, { file: convertedFile })
      const upload: UploadEntity = yield call(getUpload, uploadId)
      const image: ImageEntity = yield call(getImage, upload.imageId)

      const bestProductForImage =
        findDefaultProductForImage(image, currentAvailableProducts) ??
        currentAvailableProducts[0]

      let productId = bestProductForImage.id
      if (canUsePreferredProductId && preferredProductId) {
        productId = preferredProductId
      }

      yield call(attachUploadToPageSession, uploadId)

      yield put(
        actions.queueImageForProcessing({
          id: uploadId,
          file: convertedFile,
        })
      )

      const lineItemId: EntityId = yield call(createLineItem)

      yield put(actions.addLineItemToCart({ lineItemId, cartId: cart.id }))

      yield call(updateLineItemFromUploadWithSingleProduct, {
        lineItemId,
        uploadIds: [uploadId],
        productId,
      })

      yield call(autoDetectOrientationForLineItem, {
        lineItemId,
        imageId: image.id,
      })
    }

    yield put(
      actions.updateOrderSummary({
        reason: "created line item after upload",
        reasonType: "cartChange",
      })
    )

    yield delay(2000)

    if (!skipAutoScroll) {
      yield call(scrollToCartOrStoreSearch)
    }

    onComplete && onComplete()
  } catch (error) {
    console.error("Error creating line item from image url:", error)
  }
}

// ============================================
// replace image region from image url
// ============================================
export function* watchReplaceImageRegionImageWithImageUrl() {
  yield takeEvery(
    actions.replaceImageRegionImageWithImageUrl.type,
    processReplaceImageRegionImageWithImageUrl
  )
}

function* processReplaceImageRegionImageWithImageUrl(
  action: PayloadAction<ReplaceImageRegionImageWithImageUrlPayload>
) {
  const { imageUrl, imageRegionId, onComplete } = action.payload
  const file: File = yield call(Files.fetchImageFromUrl, imageUrl)

  yield call(() =>
    processUpdateImageRegionImage({
      type: actions.updateImageRegionImage.type,
      payload: { file, imageRegionId },
    })
  )

  onComplete && onComplete()
}

// ============================================
// fill line item image regions with
// images from  urls
// ============================================
export function* watchFillLineItemImageRegionsWithImageUrls() {
  yield takeEvery(
    actions.fillLineItemImageRegionsWithImageUrls.type,
    processFillLineItemImageRegionsWithImageUrls
  )
}

function* processFillLineItemImageRegionsWithImageUrls(
  action: PayloadAction<FillLineItemImageRegionsWithImageUrlsPayload>
) {
  const {
    lineItemId,
    imageRegionId: selectedImageRegionId,
    imageUrls,
    onComplete,
  } = action.payload

  let insertedImageCount = 0
  while (insertedImageCount < imageUrls.length) {
    const imageUrl = imageUrls[insertedImageCount]

    const isASuccess: boolean = yield call(tryToInsertImageToLineItem, {
      imageUrl,
      lineItemId,
      imageRegionId: selectedImageRegionId,
      isFirst: insertedImageCount === 0,
    })

    if (!isASuccess) {
      break
    }
    insertedImageCount++
  }

  onComplete && onComplete()
}

function* tryToInsertImageToLineItem({
  lineItemId,
  imageRegionId,
  isFirst,
  imageUrl,
}: {
  lineItemId: EntityId
  imageRegionId?: EntityId
  isFirst: boolean
  imageUrl: string
}): SagaIterator<boolean> {
  let isASuccess = false

  const lineItem: LineItemEntity = yield call(getLineItem, lineItemId)
  const printServiceProduct: PrintServiceProductEntity = yield call(
    getPrintServiceProduct,
    lineItem.productId
  )

  const imageRegionsForLineItem: ImageRegionEntity[] = yield call(
    getAllImageRegionsForLineItem,
    lineItemId
  )

  let imageRegionToStartFromIndex = 0
  if (imageRegionId) {
    const indexOfImageRegionToStartFrom = imageRegionsForLineItem.findIndex(
      imageRegion => imageRegion.id === imageRegionId
    )
    if (indexOfImageRegionToStartFrom !== -1) {
      imageRegionToStartFromIndex = indexOfImageRegionToStartFrom
    }
  }

  // find the next available image region to insert the image into
  const imageRegionsToFillBeforeCurrent = slice(
    imageRegionsForLineItem,
    0,
    imageRegionToStartFromIndex
  )

  const imageRegionsToFillAfterCurrent = slice(
    imageRegionsForLineItem,
    imageRegionToStartFromIndex,
    imageRegionsForLineItem.length
  )

  const imageRegionsToFillInOrder = [
    ...imageRegionsToFillAfterCurrent,
    ...imageRegionsToFillBeforeCurrent,
  ]
    .filter(imageRegion => !Boolean(imageRegion.uploadId))
    .filter(imageRegion => imageRegion.id !== imageRegionId)

  const nextImageRegionToFill = imageRegionsToFillInOrder[0]

  const isPhotoBook = isProductAPhotoBook(printServiceProduct)

  const insertOnCurrentImageRegion = isFirst && imageRegionId
  const insertOnNextAvailableImageRegion = Boolean(nextImageRegionToFill)
  const insertNewPageSpread = isPhotoBook

  // ===========================================
  // insert on the current image region
  // ===========================================
  if (insertOnCurrentImageRegion) {
    // insert to the selected image region
    const file: File = yield call(Files.fetchImageFromUrl, imageUrl)
    yield call(() =>
      processUpdateImageRegionImage({
        type: actions.updateImageRegionImage.type,
        payload: { file, imageRegionId },
      })
    )
    isASuccess = true
  }
  // ===========================================
  // insert on the next available region from the list
  // ===========================================
  else if (insertOnNextAvailableImageRegion) {
    const file: File = yield call(Files.fetchImageFromUrl, imageUrl)
    yield call(() =>
      processUpdateImageRegionImage({
        type: actions.updateImageRegionImage.type,
        payload: { file, imageRegionId: nextImageRegionToFill.id },
      })
    )
    isASuccess = true
  }
  // ===========================================
  // no more available image region, try to insert
  // a new page spread if possible and insert
  // the imaage to the first region of the new page spread
  // ===========================================
  else if (insertNewPageSpread) {
    // logic to insert a new page spread
    const { insidePages }: { insidePages: ProductPageEntity[] } = yield call(
      getLineItemPageSpreads,
      lineItemId
    )

    const printServiceProductImages = yield call(
      getPrintServiceProductImages,
      printServiceProduct.id
    )

    const computedData = getMinAndMaxImagesForPhotoBookProduct({
      ...printServiceProduct,
      printServiceProductImages,
    })
    const { maxPages: maxPageCount } = computedData
    const pageCount = insidePages.length
    const canAddMorePages = pageCount < maxPageCount

    const productPageIds = insidePages.map(page => page.id)
    const lastInsidePageId = last(productPageIds)

    if (lastInsidePageId && canAddMorePages) {
      const lastPageImageRegions: ImageRegionEntity[] = yield call(() =>
        getAllImageRegionsForProductPageIds([lastInsidePageId])
      )

      const [newLeftPageId, newRightPageId]: [EntityId, EntityId] = yield call(
        processInsertPageSpreadToLineItemBefore,
        {
          payload: {
            lineItemId,
            productPageId: lastInsidePageId,
            callback: (_newPageId: EntityId) => {},
          },
          type: "process/insertPageSpreadToLineItemBefore",
        }
      )

      // move last page images to the new left page
      if (lastPageImageRegions.length > 0) {
        const newSpreadImageRegions: ImageRegionEntity[] = yield call(
          getAllImageRegionsForProductPageIds,
          [newLeftPageId, newRightPageId]
        )

        for (let i = 0; i < lastPageImageRegions.length; i++) {
          const imageRegion = lastPageImageRegions[i]
          const newImageRegion = newSpreadImageRegions[i]

          yield put(
            imageRegions.actions.updateOne({
              id: imageRegion.id,
              changes: { uploadId: undefined },
            })
          )
          yield call(resetImageTransformation, {
            imageRegionId: imageRegion.id,
          })

          yield put(
            imageRegions.actions.updateOne({
              id: newImageRegion.id,
              changes: { uploadId: imageRegion.uploadId },
            })
          )
          yield call(resetImageTransformation, {
            imageRegionId: newImageRegion.id,
          })
        }
      }
    }

    // reload image regions for the line item after page spread insertion
    // and image move between pages
    const freshImageRegions: ImageRegionEntity[] = yield call(
      getAllImageRegionsForLineItem,
      lineItemId
    )

    const firstEmptyImageRegion = freshImageRegions.find(
      imageRegion => !Boolean(imageRegion.uploadId)
    )

    if (firstEmptyImageRegion) {
      const file: File = yield call(Files.fetchImageFromUrl, imageUrl)
      yield call(() =>
        processUpdateImageRegionImage({
          type: actions.updateImageRegionImage.type,
          payload: { file, imageRegionId: firstEmptyImageRegion.id },
        })
      )
      isASuccess = true
    }
  }
  // ===========================================
  // we did not found any way to insert the image
  // mark the attempt as failed
  // ===========================================
  else {
    isASuccess = false
  }

  return isASuccess
}

// ============================================
// scroll to the relevant block after line items
// have been added to the cart
// ============================================
export function* scrollToCartOrStoreSearch() {
  const pageSession: PageSessionEntity = yield call(getCurrentPageSession)
  const cart: CartEntity = yield call(getCurrentCart)

  if (
    cart.fulfillment === "pickup" &&
    pageSession.pageFlow === "store-first" &&
    !pageSession.hasLoadedStores
  ) {
    scrollTo("store-search")
  } else {
    scrollTo("cart")
  }
}
